import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import RichTextEditor from "./RichTextEditor";
import { FileText, Plus, Trash2, Sparkles } from "lucide-react";
import { API } from "../App";

const EMPTY = { name: "", type: "ia", instructions: "", tone: "chaleureux", length: "moyen", subject: "", body: "" };
const selectCls = "w-full px-3 py-2 text-sm border rounded-lg bg-background border-border";

const EmailTemplatesSection = () => {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/email-templates`, { withCredentials: true }).then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/email-templates`, form, { withCredentials: true });
      toast.success("Modèle enregistré"); setForm(EMPTY); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try { await axios.delete(`${API}/email-templates/${id}`, { withCredentials: true }); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <Card data-testid="email-templates-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><FileText className="w-5 h-5" /> Modèles d'e-mails</CardTitle>
        <Button size="sm" variant={showForm ? "outline" : "default"} onClick={() => setShowForm(!showForm)} className="gap-1.5" data-testid="new-template-btn"><Plus className="w-4 h-4" /> Nouveau modèle</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Un modèle <strong>IA</strong> pré-remplit le ton, la longueur et les instructions de génération. Un modèle <strong>Texte</strong> insère un objet et un corps fixes (variables : {"{prénom}"}, {"{nom}"}, {"{date}"}, {"{thérapeute}"}).</p>

        {showForm && (
          <div className="border rounded-lg p-4 space-y-3 bg-muted/20" data-testid="template-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Nom du modèle</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. Suivi standard" data-testid="template-name-input" /></div>
              <div><Label>Type</Label>
                <select className={selectCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="template-type-select">
                  <option value="ia">IA (préréglage)</option><option value="texte">Texte fixe</option>
                </select></div>
            </div>
            {form.type === "ia" ? (
              <>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Ton</Label>
                    <select className={selectCls} value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} data-testid="template-tone-select">
                      <option value="chaleureux">Chaleureux</option><option value="professionnel">Professionnel</option><option value="formel">Formel</option><option value="empathique">Empathique</option>
                    </select></div>
                  <div><Label>Longueur</Label>
                    <select className={selectCls} value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} data-testid="template-length-select">
                      <option value="court">Court</option><option value="moyen">Moyen</option><option value="long">Long</option>
                    </select></div>
                </div>
                <div><Label>Instructions pour l'IA</Label><Textarea rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Ex. Rappeler systématiquement l'exercice de respiration..." data-testid="template-instructions-input" /></div>
              </>
            ) : (
              <>
                <div><Label>Objet</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Suivi de notre séance du {date}" data-testid="template-subject-input" /></div>
                <div><Label>Corps</Label><RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} placeholder="Bonjour {prénom}, ..." /></div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowForm(false); setForm(EMPTY); }}>Annuler</Button>
              <Button onClick={save} disabled={saving || !form.name.trim()} data-testid="save-template-btn">{saving ? "Enregistrement..." : "Enregistrer le modèle"}</Button>
            </div>
          </div>
        )}

        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-templates">Aucun modèle pour l'instant.</p>
        ) : (
          <div className="space-y-2">
            {templates.map((tp) => (
              <div key={tp.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`template-row-${tp.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate flex items-center gap-2">{tp.name}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tp.type === "ia" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-700"}`}>{tp.type === "ia" ? <><Sparkles className="w-3 h-3 inline" /> IA</> : "Texte"}</span>
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{tp.type === "ia" ? `${tp.tone} · ${tp.length}${tp.instructions ? ` · ${tp.instructions}` : ""}` : tp.subject}</p>
                </div>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(tp.id)} data-testid={`delete-template-${tp.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailTemplatesSection;
